// components/common/dropdowns/LabeledSelectDropdown.tsx
import React from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems, Portal } from '@headlessui/react';

interface Option<T extends string> {
  value: T;
  label: string;
  description?: string;
}

interface LabeledSelectDropdownProps<T extends string> {
  label?: string;
  options: readonly Option<T>[];
  value: T | undefined;
  placeholder?: string;
  onChange: (value: T) => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

function LabeledSelectDropdown<T extends string>({
  label,
  options,
  value,
  placeholder = 'Select',
  onChange,
  disabled = false,
  loading = false,
  className = '',
}: LabeledSelectDropdownProps<T>) {
  const current = options.find((o) => o.value === value);

  return (
    <div className="w-full">
      {label && (
        <div className="text-[11px] font-medium tracking-wide uppercase text-neutral-medium mb-1.5">
          {label}
        </div>
      )}

      <Menu as="div" className="relative block w-full text-left">
        <MenuButton
          disabled={disabled || loading}
          className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg
            bg-primary-light border border-primary-subtle
            text-sm text-neutral
            hover:bg-primary-subtle/60 transition disabled:opacity-50
            focus:outline-none focus:ring-0 ${className}`}
        >
          <span className={`truncate ${current ? '' : 'text-neutral-medium'}`}>
            {loading ? 'Saving…' : current?.label ?? placeholder}
          </span>
          <ChevronDownIcon className="w-4 h-4 shrink-0 text-neutral-medium" />
        </MenuButton>

        <Portal>
          <MenuItems
            anchor="bottom start"
            className="mt-2 w-[280px]
            z-[9999]
            bg-primary-variant
            border border-primary-subtle
            rounded-lg shadow-xl
            focus:outline-none overflow-hidden"
          >
            <div className="max-h-[300px] overflow-y-auto py-1.5 px-1">
              {options.map((option) => {
                const isSelected = option.value === value;
                return (
                  <MenuItem key={option.value}>
                    {({ focus }) => (
                      <button
                        onClick={() => onChange(option.value)}
                        className={`w-full px-3 py-2 rounded text-left transition-colors flex items-start gap-2
                          ${focus ? 'bg-primary-subtle' : ''}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div
                            className={`text-sm truncate ${
                              isSelected ? 'text-neutral font-medium' : 'text-neutral'
                            }`}
                          >
                            {option.label}
                          </div>
                          {option.description && (
                            <div className="text-xs text-neutral-medium mt-0.5 line-clamp-2">
                              {option.description}
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0 mt-1.5" />
                        )}
                      </button>
                    )}
                  </MenuItem>
                );
              })}
            </div>
          </MenuItems>
        </Portal>
      </Menu>
    </div>
  );
}

export default LabeledSelectDropdown;